import { t as uiText } from '../i18n';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useFolderContext } from '../context/FolderContext';
import { useFolderPageCounts } from '../features/folder/useFolderPageCounts';
import { FolderData } from '../types/folder';
import FolderTree from './FolderTree';

interface FolderMoveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (folderId: string | null) => void;
  selectedPageCount: number;
  currentFolderId?: string | null; // 現在のフォルダーID
}

const FolderMoveModal: React.FC<FolderMoveModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedPageCount,
  currentFolderId
}) => {
  const { workspaceId } = useParams();
  const { folders } = useFolderContext();
  const { folderPageCounts } = useFolderPageCounts(workspaceId);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [openFolderIds, setOpenFolderIds] = useState<Set<string>>(new Set());

  // モーダルが開かれるたびに現在のフォルダーを選択状態にする
  useEffect(() => {
    if (isOpen) {
      // フォルダ情報がない場合はルート（null）を選択状態にする
      const folderIdToSelect = (currentFolderId === 'root' || !currentFolderId) ? null : currentFolderId;
      setSelectedFolderId(folderIdToSelect);
      
      // 現在のフォルダーの祖先フォルダーを開く
      if (currentFolderId && currentFolderId !== 'root') {
        const currentFolder = folders.find(f => f.id === currentFolderId);
        if (currentFolder && currentFolder.ancestorFolderIds) {
          setOpenFolderIds(new Set(currentFolder.ancestorFolderIds));
        } else {
          setOpenFolderIds(new Set());
        }
      } else {
        // ルートフォルダーの場合は何も開かない
        setOpenFolderIds(new Set());
      }
    }
  }, [isOpen, currentFolderId, folders]);

  // フォルダーの開閉を切り替える関数
  const toggleFolderOpen = (folderId: string) => {
    setOpenFolderIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  // ルートフォルダー（フォルダーなし）の選択
  const handleRootSelection = () => {
    setSelectedFolderId(null);
  };

  // 確認処理
  const handleConfirm = () => {
    onConfirm(selectedFolderId);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#fff',
          padding: '24px',
          borderRadius: '8px',
          minWidth: '500px',
          maxWidth: '90%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ 
          marginTop: 0, 
          marginBottom: '16px',
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#333'
        }}>
          {uiText("Move to folder")}
        </h3>
        
        <p style={{ 
          marginBottom: '20px', 
          color: '#666',
          fontSize: '14px'
        }}>
          {uiText("Selected")} {selectedPageCount} {uiText("pages. Select a destination folder.")}
        </p>

        {/* ルートフォルダー（フォルダーなし）の選択 */}
        <div
          onClick={handleRootSelection}
          style={{
            padding: '8px 12px',
            cursor: 'pointer',
            backgroundColor: selectedFolderId === null ? 'var(--workspace-primary, #cfae91)' : '#f8f9fa',
            borderRadius: '4px',
            border: selectedFolderId === null ? '2px solid var(--workspace-primary, #cfae91)' : '1px solid #e9ecef',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px'
          }}
          onMouseEnter={(e) => {
            if (selectedFolderId !== null) {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedFolderId !== null) {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }
          }}
        >
          <span style={{ fontSize: '14px', color: selectedFolderId === null ? '#fff' : '#666' }}>📄</span>
          <span style={{ 
            fontSize: '14px', 
            fontWeight: selectedFolderId === null ? 'bold' : 'normal',
            color: selectedFolderId === null ? '#fff' : '#333'
          }}>
            {uiText("No folder (root)")}
          </span>
        </div>

        {/* フォルダーツリー */}
        <div style={{ 
          maxHeight: '400px', 
          overflowY: 'auto',
          border: '1px solid #e9ecef',
          borderRadius: '4px',
          padding: '8px',
          backgroundColor: '#f8f9fa'
        }}>
          <FolderTree
            folders={folders}
            folderPageCounts={folderPageCounts}
            onFolderSelect={(folderId) => setSelectedFolderId(folderId)}
            selectedFolderId={selectedFolderId}
            showSelectionMode={true}
            openFolderIds={openFolderIds}
            setOpenFolderIds={setOpenFolderIds}
            toggleFolderOpen={toggleFolderOpen}
          />
        </div>

        {/* ボタン */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'flex-end',
          marginTop: '20px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#ccc',
              color: '#000',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {uiText("Cancel")}
          </button>
          <button
            onClick={handleConfirm}
            style={{
              padding: '8px 16px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {uiText("Move")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FolderMoveModal;
